/**
 * Markdown Formatter Utility
 * Converts simple markdown syntax to React elements
 */

import React from 'react';

/**
 * Format markdown text to React elements
 * Handles:
 * - **bold text** → <strong>bold text</strong>
 * - * bullet points → <li>bullet points</li>
 * - - bullet points → <li>bullet points</li>
 * - Line breaks
 */
export function formatMarkdown(text: string): React.ReactNode {
  return <div className="text-sm leading-relaxed">{formatMarkdownContent(text)}</div>;
}

function formatMarkdownContent(text: string): React.ReactNode {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let bulletItems: string[] = [];
  let key = 0;

  const flushBulletList = () => {
    if (bulletItems.length > 0) {
      elements.push(
        <ul key={`ul-${key++}`} className="list-disc list-inside space-y-1 my-2 text-inherit">
          {bulletItems.map((item, idx) => (
            <li key={`li-${idx}`}>
              {formatInlineMarkdown(item)}
            </li>
          ))}
        </ul>
      );
      bulletItems = [];
    }
  };

  lines.forEach((line, lineIndex) => {
    const trimmedLine = line.trim();
    
    // Check if line starts with * or - (bullet point)
    if (trimmedLine.startsWith('* ') || trimmedLine.startsWith('- ')) {
      bulletItems.push(trimmedLine.substring(2));
    } else {
      // Flush any accumulated bullet items
      flushBulletList();
      
      // Regular line with inline markdown
      if (trimmedLine) {
        elements.push(
          <p key={`p-${key++}`} className="m-0">
            {formatInlineMarkdown(trimmedLine)}
          </p>
        );
      } else if (lineIndex < lines.length - 1) {
        // Empty line - add spacing
        elements.push(<br key={`br-${key++}`} />);
      }
    }
  });

  // Flush any remaining bullet items
  flushBulletList();

  return <>{elements}</>;
}

/**
 * Format inline markdown (bold text)
 * **text** → <strong>text</strong>
 */
function formatInlineMarkdown(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let currentText = '';
  let i = 0;
  let key = 0;

  while (i < text.length) {
    // Check for **bold**
    if (text[i] === '*' && text[i + 1] === '*') {
      // Save any accumulated text
      if (currentText) {
        parts.push(currentText);
        currentText = '';
      }

      // Find closing **
      let j = i + 2;
      while (j < text.length - 1) {
        if (text[j] === '*' && text[j + 1] === '*') {
          // Found closing
          const boldText = text.substring(i + 2, j);
          parts.push(
            <strong key={`bold-${key++}`} className="font-semibold">
              {boldText}
            </strong>
          );
          i = j + 2;
          break;
        }
        j++;
      }

      // If no closing found, treat as regular text
      if (j >= text.length - 1) {
        currentText += text[i];
        i++;
      }
    } else {
      currentText += text[i];
      i++;
    }
  }

  // Add any remaining text
  if (currentText) {
    parts.push(currentText);
  }

  return <>{parts}</>;
}

